import { useFeatureEnabled, useShouldAutoNavigateOnInit } from 'api/appConfig';
import { useSSOConfigCardNavigate, useStepNavigate } from 'utils/navigation';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GuideType, Method, SCIMMethod } from 'consts';
import { QueryParamK } from 'keys';
import { S4Feature } from 'types/api';
import useEffectOnceWhen from './useEffectOnceWhen';

const parseTargetParam = (
	target: string | null
): {
	guideType: GuideType;
	idpId: string;
	method: Method | SCIMMethod;
} | null => {
	if (!target) return null;

	const parts = target.split(':');
	if (parts.length !== 3) return null;

	const [guideTypeStr, idpId, methodStr] = parts;

	if (!idpId?.trim()) return null;

	if (!Object.values(GuideType).includes(guideTypeStr as GuideType))
		return null;
	const guideType = guideTypeStr as GuideType;

	if (guideType === GuideType.SSO) {
		if (!Object.values(Method).includes(methodStr as Method)) return null;
		return { guideType, idpId, method: methodStr as Method };
	}

	if (guideType === GuideType.SCIM) {
		if (!Object.values(SCIMMethod).includes(methodStr as SCIMMethod))
			return null;
		return { guideType, idpId, method: methodStr as SCIMMethod };
	}

	/* istanbul ignore next */
	return null;
};

export const useAutoNavigateOnAppInit = (enabled: boolean) => {
	const ssoConfigCardNavigate = useSSOConfigCardNavigate();
	const shouldAutoNavigate = useShouldAutoNavigateOnInit();
	const { navigateToStep, navigateToScimStep } = useStepNavigate();
	const [searchParams] = useSearchParams();

	const { enabled: ssoEnabled } = useFeatureEnabled(S4Feature.Sso);
	const { enabled: scimEnabled } = useFeatureEnabled(S4Feature.Scim);
	const { enabled: samlEnabled } = useFeatureEnabled(S4Feature.Saml);
	const { enabled: oidcEnabled } = useFeatureEnabled(S4Feature.Oidc);

	const isMethodEnabled = useCallback(
		(guideType: GuideType, method: Method | SCIMMethod): boolean => {
			if (guideType === GuideType.SSO && !ssoEnabled) return false;
			if (guideType === GuideType.SCIM && !scimEnabled) return false;
			if (method === Method.Saml || method === SCIMMethod.Saml)
				return samlEnabled;
			if (method === Method.Oidc || method === SCIMMethod.Oidc)
				return oidcEnabled;
			return true;
		},
		[ssoEnabled, scimEnabled, samlEnabled, oidcEnabled]
	);

	const handleTargetParam = useCallback((): boolean => {
		const target = searchParams.get(QueryParamK.Target);
		const parsed = parseTargetParam(target);

		if (!parsed) {
			return false;
		}
		if (!isMethodEnabled(parsed.guideType, parsed.method)) {
			return false;
		}

		if (parsed.guideType === GuideType.SSO) {
			navigateToStep(1, parsed.idpId, parsed.method as Method);
		} else {
			navigateToScimStep(1, parsed.idpId, parsed.method as SCIMMethod);
		}

		return true;
	}, [searchParams, isMethodEnabled, navigateToStep, navigateToScimStep]);

	const handleAutoNavigate = useCallback(() => {
		// target query param takes precedent.
		if (handleTargetParam()) {
			return;
		}

		if (!shouldAutoNavigate) {
			return;
		}
		ssoConfigCardNavigate();
	}, [handleTargetParam, shouldAutoNavigate, ssoConfigCardNavigate]);

	useEffectOnceWhen(enabled, handleAutoNavigate, [shouldAutoNavigate, enabled]);
};
